import { FC, useEffect, useState } from 'react';

import { Box, Chip, Grid, SelectChangeEvent, Typography } from '@mui/material';

import {
	BlogList as BlogListDetails,
	BlogListPageDescription,
	Brand,
	ContentTypeRichTextContentLinks,
	BlogListPageContentListCollection,
	BlogListPageContentAfterListCollection,
} from '@/types/generated';
import { getAllBlogCategoryListByBrand } from '@/services/getAllBlogCategoryListByBrand';
import { getAllBlogTagListByBrand } from '@/services/getAllBlogTagListByBrand';
import { useAppContext } from '@/context';
import { logWarning } from '@/utils/miscUtils';
import { getNumberOfBlogs } from '@/utils';

import { ICardDesignType } from '../BlogList/BlogListItem';
import BlogList, { IBlogList } from '../BlogList/BlogList';
import RichText, { RichTextContent } from '../RichText/RichText';
import SingleMessage, { ISingleMessage } from '../SingleMessage/SingleMessage';
import { getHrefPath } from '../TAGLink/TAGLink.helpers';
import BlogTagDropdown from './BlogTagDropdown';

import { chipStyles, filterListStyles, getTitleContainerStyles, titleStyles } from './BlogListPage.styles';

const getInitialStartIndex = (currentPage: number, pageSize: number) => (currentPage - 1) * pageSize;

interface IBlogListDetails extends BlogListDetails {
	cardDesignType?: ICardDesignType;
}
export interface IBlogListPage {
	blogList: IBlogListDetails;
	brand: Brand;
	title?: string;
	description?: BlogListPageDescription;
	preview?: boolean;
	contentListCollection?: BlogListPageContentListCollection;
	contentAfterListCollection?: BlogListPageContentAfterListCollection;
	shouldDisplayCategoryFilterChips?: boolean;
	currentPage?: number;
}

export interface IBlogContentListItem extends BlogListDetails {
	startIndexBlogList: number;
	setStartIndexBlogList: (startIndex: number) => void;
}

const contentListItemRenderer = [
	{
		name: 'BlogList',
		renderer: (item: IBlogContentListItem) => {
			return <BlogList {...(item as IBlogList)} />;
		},
	},
];

const contentAfterListItemRenderer = [
	{
		name: 'SingleMessage',
		renderer: (item: ISingleMessage) => {
			return <SingleMessage {...item} />;
		},
	},
];

const ContentListItemByType = (props: { contentItem: IBlogContentListItem }) => {
	const { contentItem } = props;

	const typename = contentItem?.__typename;
	if (!typename) return null;
	const result = contentListItemRenderer.find((r) => typename === r.name);
	return result ? result.renderer(contentItem) : null;
};

const ContentAfterListItemByType = (props: { contentItem: ISingleMessage }) => {
	const { contentItem } = props;

	const typename = contentItem?.__typename;
	if (!typename) return null;
	const result = contentAfterListItemRenderer.find((r) => typename === r.name);
	return result ? result.renderer(contentItem) : null;
};

type TCategoryChipType = 'category' | 'subCategory' | 'all';

interface ICategoryChip {
	name: string;
	type: TCategoryChipType;
}

const CategoryChip: FC<{
	categoryChip: ICategoryChip;
	selectedCategoryName?: string;
	onChipClick: (categoryChip: ICategoryChip) => void;
}> = ({ categoryChip, selectedCategoryName, onChipClick }) => (
	<Chip
		key={categoryChip.name}
		variant={selectedCategoryName === categoryChip.name ? 'selected' : 'input'}
		sx={chipStyles}
		onClick={() => categoryChip.name && onChipClick(categoryChip)}
		clickable
		label={categoryChip.name}
		data-testid={`blog_list_page_tag_${categoryChip.name ?? ''}`}
	/>
);

const allBlogsCategoryChip: ICategoryChip = {
	name: 'All Blogs',
	type: 'all',
};

export default function BlogListPage({
	blogList,
	title,
	contentListCollection,
	description,
	preview = false,
	shouldDisplayCategoryFilterChips = false,
	contentAfterListCollection,
	currentPage = 1,
}: Readonly<IBlogListPage>) {
	const { environment, config } = useAppContext();
	const contentfulGraphqlURL =
		environment === 'prod'
			? config.services.prod.CONTENTFUL_GRAPHQL_URL
			: config.services.nonprod.CONTENTFUL_GRAPHQL_URL;
	const blogFeatureConfig = config.features?.blogs;
	const blogRootPath = blogFeatureConfig?.root ?? '/';
	const blogPageSize = getNumberOfBlogs('Blog List Page', blogFeatureConfig);
	const useTagLinks = !!blogFeatureConfig?.useTagLinks;
	const blogTagPath = `${blogRootPath}tag/`;
	const migrationPaths = config?.featureFlags?.migrationPaths as string[] | undefined;

	// brand name comes from blog list content itself or take it from context
	const brandNameForQuery = blogList.brand?.title ?? config.name ?? '';

	const [categoriesData, setCategoriesData] = useState<ICategoryChip[]>([]);
	const [categoriesDataFetched, setCategoriesDataFetched] = useState(false);
	const [tagsData, setTagsData] = useState<string[]>([]);
	const [tagsDataFetched, setTagsDataFetched] = useState(false);
	const [selectedTagPath, setSelectedTagPath] = useState('');
	const [selectedCategory, setSelectedCategory] = useState<ICategoryChip | null>(allBlogsCategoryChip);
	const [startIndexBlogList, setStartIndexBlogList] = useState(() => getInitialStartIndex(currentPage, blogPageSize));

	const shouldRenderContainer = !!title || !!description || !!categoriesData.length || !!tagsData.length;
	const hasContentListItems =
		contentListCollection?.items && contentListCollection?.items?.length > 0 && selectedCategory?.type === 'all';
	const hasContentAfterListItems = contentAfterListCollection?.items && contentAfterListCollection?.items?.length > 0;

	const onChipClick = (categoryChip: ICategoryChip) => {
		setSelectedCategory(categoryChip);
		setStartIndexBlogList(0);
	};

	const handleTagChange = (event: SelectChangeEvent<string>) => {
		const nextTagPath = event.target.value;

		setSelectedTagPath(nextTagPath);

		if (nextTagPath) {
			globalThis.window.location.href = getHrefPath(nextTagPath, migrationPaths);
		}
	};

	const fetchCategoryData = async () => {
		try {
			const categoryListResponse = await getAllBlogCategoryListByBrand(
				brandNameForQuery,
				contentfulGraphqlURL,
				preview
			);

			const categoryList = categoryListResponse.categoriesResponse?.blogCategoryCollection?.items;
			const subCategoryList = categoryListResponse.subCategoriesResponse?.blogSubCategoryCollection?.items;

			if (categoryList || subCategoryList) {
				const categoryChipsList: ICategoryChip[] =
					categoryList?.map((category) => ({ name: category.name ?? '', type: 'category' })) ?? [];
				const subCategoriesChipsList: ICategoryChip[] =
					subCategoryList?.map((subCategory) => ({
						name: subCategory.name ?? '',
						type: 'subCategory',
					})) ?? [];

				setCategoriesData([allBlogsCategoryChip, ...categoryChipsList, ...subCategoriesChipsList]);
			}
		} catch (error) {
			// Handle any errors that occurred during the API call.
			// eslint-disable-next-line @typescript-eslint/restrict-template-expressions
			logWarning(`Error fetching blog category list by brand error: ${error}`);
		} finally {
			setCategoriesDataFetched(true);
		}
	};

	useEffect(() => {
		if (shouldDisplayCategoryFilterChips && !categoriesDataFetched) {
			void fetchCategoryData();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldDisplayCategoryFilterChips]);

	useEffect(() => {
		const fetchTagsData = async () => {
			try {
				const siteTags = await getAllBlogTagListByBrand(brandNameForQuery, contentfulGraphqlURL, preview);
				setTagsData(siteTags);
			} catch (error) {
				logWarning(`Error fetching blog tag list by brand error: ${error}`);
			} finally {
				setTagsDataFetched(true);
			}
		};

		if (useTagLinks && !tagsDataFetched) {
			void fetchTagsData();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [useTagLinks]);

	return (
		<>
			{shouldRenderContainer && (
				<Grid container sx={getTitleContainerStyles(!!description)}>
					{title && (
						<Typography
							component="h1"
							variant="heroMedium"
							color="text.primary"
							data-testid="blog_list_page_title"
							sx={titleStyles}
						>
							{title}
						</Typography>
					)}
					{description && (
						<Box
							data-testid={'blog_list_page_description'}
							sx={{
								width: '100%',
							}}
						>
							<RichText
								links={description?.links as ContentTypeRichTextContentLinks}
								content={description?.json as RichTextContent}
								docProps={{ color: 'text.secondary', variant: 'bodyMediumBook', textAlign: 'center' }}
							/>
						</Box>
					)}
					<BlogTagDropdown
						tags={tagsData}
						blogTagPath={blogTagPath}
						selectedTagPath={selectedTagPath}
						onTagChange={handleTagChange}
					/>
					{!!categoriesData.length && (
						<Box data-testid="blog_list_page_tags" sx={filterListStyles}>
							{categoriesData.map((categoryChip) => (
								<CategoryChip
									key={categoryChip.name}
									categoryChip={categoryChip}
									selectedCategoryName={selectedCategory?.name}
									onChipClick={onChipClick}
								/>
							))}
						</Box>
					)}
				</Grid>
			)}
			{hasContentListItems && (
				<Box
					data-testid="blog_list_page_content_list"
					sx={{
						marginTop: '2rem',
						backgroundColor: 'backgrounds.light',
					}}
				>
					{contentListCollection.items.map((item, index) =>
						item ? (
							<ContentListItemByType
								contentItem={{ ...item, startIndexBlogList, setStartIndexBlogList }}
								key={`${item?.sys?.id || ''}-${index}`}
							/>
						) : null
					)}
				</Box>
			)}
			<BlogList
				{...blogList}
				preview={preview}
				startIndexBlogList={startIndexBlogList}
				setStartIndexBlogList={setStartIndexBlogList}
				categoryName={selectedCategory?.type === 'category' ? selectedCategory.name : undefined}
				subCategoryName={selectedCategory?.type === 'subCategory' ? selectedCategory.name : undefined}
			/>
			{hasContentAfterListItems && (
				<Box data-testid="blog_list_page_content_after_list">
					{contentAfterListCollection.items.map((item, index) =>
						item ? (
							<ContentAfterListItemByType
								contentItem={{ ...item }}
								key={`${item?.sys?.id ?? ''}-${index}`}
							/>
						) : null
					)}
				</Box>
			)}
		</>
	);
}
